import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Banknote,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  RefreshCw,
  Settings,
  Calendar,
  DollarSign,
  Users,
  Loader2,
  Play,
  ChevronRight,
  Shield,
  Zap,
  History,
  TrendingUp
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const SoldierPayoutDashboard = ({ token }) => {
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [settings, setSettings] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [autoApproving, setAutoApproving] = useState(false);
  const [showConfirmProcess, setShowConfirmProcess] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);

  useEffect(() => {
    if (token) {
      fetchDashboard();
    }
  }, [token]);

  const fetchDashboard = async () => {
    if (!token) {
      setLoading(false);
      return;
    }
    try {
      const response = await axios.get(`${API}/soldiers/admin/payout-dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDashboard(response.data);
      setSettings(response.data.settings);
    } catch (error) {
      console.error("Failed to fetch payout dashboard:", error);
      toast.error("Failed to load payout dashboard");
    } finally {
      setLoading(false);
    }
  };

  const handleAutoApprove = async () => {
    setAutoApproving(true);
    try {
      const response = await axios.post(`${API}/soldiers/admin/auto-approve-payouts`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data.message);
      fetchDashboard();
    } catch (error) {
      console.error("Auto-approve failed:", error);
      toast.error(error.response?.data?.detail || "Auto-approve failed");
    } finally {
      setAutoApproving(false);
    }
  };

  const handleBatchProcess = async (force = false) => {
    setProcessing(true);
    setShowConfirmProcess(false);
    try {
      const response = await axios.post(`${API}/soldiers/admin/batch-process-payouts`, 
        { force },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      
      if (response.data.processed > 0) {
        toast.success(`Processed ${response.data.processed} payouts - $${response.data.total_amount}`);
      } else if (response.data.is_payout_day === false) {
        toast.info(response.data.message);
        setShowConfirmProcess(true);
      } else {
        toast.info(response.data.message);
      }
      fetchDashboard();
    } catch (error) {
      console.error("Batch process failed:", error);
      toast.error(error.response?.data?.detail || "Batch process failed");
    } finally {
      setProcessing(false);
    }
  };

  const handleSaveSettings = async () => {
    setSavingSettings(true);
    try {
      await axios.put(`${API}/soldiers/admin/payout-settings`, settings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Payout settings saved");
      setShowSettings(false);
      fetchDashboard();
    } catch (error) {
      console.error("Failed to save settings:", error);
      toast.error(error.response?.data?.detail || "Failed to save settings");
    } finally {
      setSavingSettings(false);
    }
  };

  const handleApproveSingle = async (payoutId) => {
    try {
      await axios.post(`${API}/soldiers/admin/payouts/${payoutId}/approve`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Payout approved");
      fetchDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to approve");
    }
  };

  const handleProcessSingle = async (payoutId) => {
    try {
      await axios.post(`${API}/soldiers/admin/payouts/${payoutId}/process`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Payout processed successfully");
      fetchDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to process");
    }
  };

  if (loading) {
    return (
      <Card className="bg-zinc-900 border-zinc-800">
        <CardContent className="p-8 flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6" data-testid="soldier-payout-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white flex items-center gap-3">
            <Banknote className="w-7 h-7 text-green-400" />
            Soldier Payouts
          </h2>
          <p className="text-zinc-400 mt-1">Manage commission payouts to HairChair Soldiers</p>
        </div>
        <Button
          variant="outline"
          className="border-zinc-700 text-zinc-300"
          onClick={() => setShowSettings(true)}
        >
          <Settings className="w-4 h-4 mr-2" />
          Settings
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {/* Ready to Pay */}
        <Card className="bg-gradient-to-br from-green-500/20 to-emerald-500/10 border-green-500/30">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-green-400 text-sm font-medium">Ready to Pay</p>
                <p className="text-3xl font-bold text-white mt-1">
                  ${dashboard?.ready_to_pay?.total_amount?.toFixed(2) || "0.00"}
                </p>
                <p className="text-zinc-400 text-xs mt-1">
                  {dashboard?.ready_to_pay?.count || 0} approved payouts
                </p>
              </div>
              <div className="w-12 h-12 bg-green-500/20 rounded-full flex items-center justify-center">
                <CheckCircle className="w-6 h-6 text-green-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Pending Verification */}
        <Card className="bg-gradient-to-br from-amber-500/20 to-orange-500/10 border-amber-500/30">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-amber-400 text-sm font-medium">Pending Verification</p>
                <p className="text-3xl font-bold text-white mt-1">
                  ${dashboard?.pending_verification?.total_amount?.toFixed(2) || "0.00"}
                </p>
                <p className="text-zinc-400 text-xs mt-1">
                  {dashboard?.pending_verification?.count || 0} referrals in {dashboard?.settings?.verification_days || 30}-day hold
                </p>
              </div>
              <div className="w-12 h-12 bg-amber-500/20 rounded-full flex items-center justify-center">
                <Clock className="w-6 h-6 text-amber-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Total Paid */}
        <Card className="bg-gradient-to-br from-blue-500/20 to-indigo-500/10 border-blue-500/30">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-blue-400 text-sm font-medium">Total Paid (All Time)</p>
                <p className="text-3xl font-bold text-white mt-1">
                  ${dashboard?.total_paid_all_time?.toFixed(2) || "0.00"}
                </p>
                <p className="text-zinc-400 text-xs mt-1">
                  {dashboard?.payout_counts?.paid || 0} completed payouts
                </p>
              </div>
              <div className="w-12 h-12 bg-blue-500/20 rounded-full flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-blue-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Next Payout Date */}
        <Card className="bg-gradient-to-br from-purple-500/20 to-pink-500/10 border-purple-500/30">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-purple-400 text-sm font-medium">Next Payout</p>
                <p className="text-xl font-bold text-white mt-1">
                  {dashboard?.schedule?.next_payout_date || "Not set"}
                </p>
                <p className="text-zinc-400 text-xs mt-1 capitalize">
                  {dashboard?.schedule?.frequency} on {dashboard?.schedule?.day}
                </p>
              </div>
              <div className="w-12 h-12 bg-purple-500/20 rounded-full flex items-center justify-center">
                <Calendar className="w-6 h-6 text-purple-400" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Action Buttons */}
      <Card className="bg-zinc-900 border-zinc-800">
        <CardHeader>
          <CardTitle className="text-white text-lg">Quick Actions</CardTitle>
          <CardDescription>Run auto-approve or process approved payouts</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap gap-4">
            {/* Auto-Approve Button */}
            <Button
              onClick={handleAutoApprove}
              disabled={autoApproving}
              className="bg-amber-500 hover:bg-amber-600 text-black font-medium"
            >
              {autoApproving ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Zap className="w-4 h-4 mr-2" />
              )}
              Auto-Approve Verified Commissions
            </Button>

            {/* Process Payouts Button */}
            <Button
              onClick={() => handleBatchProcess(false)}
              disabled={processing || (dashboard?.ready_to_pay?.count || 0) === 0}
              className="bg-green-500 hover:bg-green-600 text-black font-medium"
            >
              {processing ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Play className="w-4 h-4 mr-2" />
              )}
              Process All Approved (${dashboard?.ready_to_pay?.total_amount?.toFixed(2) || "0"})
            </Button>

            {/* Refresh Button */}
            <Button
              variant="outline"
              onClick={fetchDashboard}
              className="border-zinc-700 text-zinc-300"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>

          {/* How It Works */}
          <div className="p-4 bg-zinc-800/50 rounded-lg border border-zinc-700/50">
            <p className="text-sm font-medium text-white mb-2 flex items-center gap-2">
              <Shield className="w-4 h-4 text-blue-400" />
              How the Hybrid Auto-Payout Works:
            </p>
            <ol className="text-sm text-zinc-400 space-y-1 list-decimal list-inside">
              <li>Soldier earns commission from referral purchase</li>
              <li>Commission held for <strong className="text-amber-400">{dashboard?.settings?.verification_days || 30} days</strong> (chargeback protection)</li>
              <li>Click <strong className="text-amber-400">&quot;Auto-Approve&quot;</strong> to approve all verified commissions</li>
              <li>On <strong className="text-purple-400 capitalize">{dashboard?.schedule?.day}</strong>, click <strong className="text-green-400">&quot;Process All Approved&quot;</strong> to send payments</li>
              <li>Stripe transfers funds to Soldier&apos;s connected bank account</li>
            </ol>
          </div>
        </CardContent>
      </Card>

      {/* Approved Payouts Ready to Process */}
      {(dashboard?.ready_to_pay?.payouts?.length || 0) > 0 && (
        <Card className="bg-zinc-900 border-zinc-800">
          <CardHeader>
            <CardTitle className="text-white text-lg flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-400" />
              Approved Payouts - Ready to Process
            </CardTitle>
            <CardDescription>
              {dashboard.ready_to_pay.count} payouts totaling ${dashboard.ready_to_pay.total_amount.toFixed(2)}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {dashboard.ready_to_pay.payouts.map((payout) => (
                <div
                  key={payout.payout_id}
                  className="p-4 bg-zinc-800/50 rounded-lg border border-zinc-700/50"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 bg-green-500/20 rounded-full flex items-center justify-center">
                        <DollarSign className="w-5 h-5 text-green-400" />
                      </div>
                      <div>
                        <p className="text-white font-medium">
                          {payout.soldier_name || payout.soldier_email || payout.soldier_id}
                        </p>
                        <p className="text-zinc-400 text-sm">
                          {payout.commission_breakdown?.description || payout.payout_type || "Commission"} • 
                          {payout.approved_at ? ` Approved ${new Date(payout.approved_at).toLocaleDateString()}` : ""}
                        </p>
                        {payout.soldier_salon && (
                          <p className="text-zinc-500 text-xs mt-1">Salon: {payout.soldier_salon}</p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <p className="text-xl font-bold text-green-400">${payout.amount.toFixed(2)}</p>
                        {payout.auto_approved && (
                          <Badge className="bg-amber-500/20 text-amber-400 text-xs">Auto-approved</Badge>
                        )}
                      </div>
                      <Button
                        size="sm"
                        onClick={() => handleProcessSingle(payout.payout_id)}
                        className="bg-green-500 hover:bg-green-600 text-black"
                        disabled={!dashboard?.can_process_payouts}
                      >
                        <Play className="w-3 h-3 mr-1" />
                        Pay Now
                      </Button>
                    </div>
                  </div>
                  {/* Referral Details Row */}
                  {payout.referral_details && (
                    <div className="mt-3 pt-3 border-t border-zinc-700/50 grid grid-cols-3 gap-4 text-xs">
                      <div>
                        <span className="text-zinc-500">Referred:</span>
                        <span className="text-zinc-300 ml-1">{payout.referral_details.referred_email || "N/A"}</span>
                      </div>
                      <div>
                        <span className="text-zinc-500">Tier:</span>
                        <Badge className={`ml-1 text-xs ${payout.referral_details.subscription_tier === 'elite' ? 'bg-amber-500/20 text-amber-400' : 'bg-purple-500/20 text-purple-400'}`}>
                          {payout.referral_details.subscription_tier || "Unknown"}
                        </Badge>
                      </div>
                      <div>
                        <span className="text-zinc-500">Month:</span>
                        <span className="text-zinc-300 ml-1">{payout.referral_details.payment_month || "N/A"}</span>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Pending Verification Details */}
      {(dashboard?.pending_verification?.referrals?.length || 0) > 0 && (
        <Card className="bg-zinc-900 border-zinc-800">
          <CardHeader>
            <CardTitle className="text-white text-lg flex items-center gap-2">
              <Clock className="w-5 h-5 text-amber-400" />
              Pending Verification ({dashboard.pending_verification.count})
            </CardTitle>
            <p className="text-zinc-400 text-sm">
              These referrals are within the {dashboard.settings?.verification_days || 30}-day vetting period
            </p>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {dashboard.pending_verification.referrals.slice(0, 10).map((ref) => (
                <div
                  key={ref.referral_id}
                  className="flex items-center justify-between p-3 bg-zinc-800/30 rounded-lg"
                >
                  <div className="flex items-center gap-3">
                    <Clock className="w-4 h-4 text-amber-400" />
                    <div>
                      <p className="text-white text-sm">{ref.referred_email || ref.referral_id}</p>
                      <p className="text-zinc-500 text-xs">
                        {ref.days_until_verified} days until verified • 
                        <Badge className={`ml-1 text-xs ${ref.subscription_tier === 'elite' ? 'bg-amber-500/20 text-amber-400' : 'bg-purple-500/20 text-purple-400'}`}>
                          {ref.subscription_tier || "Unknown"}
                        </Badge>
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-amber-400 font-medium">${ref.total_pending?.toFixed(2) || "0.00"}</p>
                    <p className="text-zinc-500 text-xs">pending</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Commission Summary */}
      {dashboard?.commission_summary && (
        <Card className="bg-zinc-900 border-zinc-800">
          <CardHeader>
            <CardTitle className="text-white text-lg flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-purple-400" />
              Commission Breakdown (All Time)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-4">
              <div className="text-center p-4 bg-zinc-800/50 rounded-lg">
                <p className="text-2xl font-bold text-green-400">
                  ${dashboard.commission_summary.month1_bonuses_paid?.toFixed(2) || "0.00"}
                </p>
                <p className="text-zinc-400 text-sm">Month 1 Bonuses</p>
              </div>
              <div className="text-center p-4 bg-zinc-800/50 rounded-lg">
                <p className="text-2xl font-bold text-blue-400">
                  ${dashboard.commission_summary.month2_releases_paid?.toFixed(2) || "0.00"}
                </p>
                <p className="text-zinc-400 text-sm">Month 2 Releases</p>
              </div>
              <div className="text-center p-4 bg-zinc-800/50 rounded-lg">
                <p className="text-2xl font-bold text-purple-400">
                  ${dashboard.commission_summary.lifetime_commissions_paid?.toFixed(2) || "0.00"}
                </p>
                <p className="text-zinc-400 text-sm">Lifetime (8%)</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Recent Payouts */}
      {(dashboard?.recent_payouts?.length || 0) > 0 && (
        <Card className="bg-zinc-900 border-zinc-800">
          <CardHeader>
            <CardTitle className="text-white text-lg flex items-center gap-2">
              <History className="w-5 h-5 text-blue-400" />
              Recent Payouts
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {dashboard.recent_payouts.map((payout) => (
                <div
                  key={payout.payout_id}
                  className="flex items-center justify-between p-3 bg-zinc-800/30 rounded-lg"
                >
                  <div className="flex items-center gap-3">
                    <CheckCircle className="w-4 h-4 text-green-400" />
                    <div>
                      <p className="text-white text-sm">
                        {payout.soldier_name || payout.soldier_id}
                      </p>
                      <p className="text-zinc-500 text-xs">
                        {payout.commission_type_label || payout.payout_type} • 
                        Paid {payout.paid_at ? new Date(payout.paid_at).toLocaleDateString() : ""}
                      </p>
                    </div>
                  </div>
                  <p className="text-green-400 font-medium">${payout.amount.toFixed(2)}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Settings Dialog */}
      <Dialog open={showSettings} onOpenChange={setShowSettings}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings className="w-5 h-5 text-amber-400" />
              Payout Settings
            </DialogTitle>
            <DialogDescription className="text-zinc-400">
              Configure the auto-payout schedule and verification period
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Verification Period */}
            <div className="space-y-2">
              <Label className="text-zinc-300">Verification Period (Days)</Label>
              <Input
                type="number"
                value={settings?.verification_days || 30}
                onChange={(e) => setSettings({ ...settings, verification_days: parseInt(e.target.value) || 30 })}
                className="bg-zinc-800 border-zinc-700 text-white"
                min={7}
                max={90}
              />
              <p className="text-xs text-zinc-500">
                Commissions are held for this many days to protect against chargebacks/refunds
              </p>
            </div>

            {/* Payout Schedule */}
            <div className="space-y-2">
              <Label className="text-zinc-300">Payout Schedule</Label>
              <Select
                value={settings?.payout_schedule || "weekly"}
                onValueChange={(value) => setSettings({ ...settings, payout_schedule: value })}
              >
                <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-800 border-zinc-700">
                  <SelectItem value="weekly">Weekly</SelectItem>
                  <SelectItem value="biweekly">Bi-Weekly</SelectItem>
                  <SelectItem value="monthly">Monthly</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Payout Day */}
            <div className="space-y-2">
              <Label className="text-zinc-300">
                {settings?.payout_schedule === "monthly" ? "Day of Month" : "Day of Week"}
              </Label>
              {settings?.payout_schedule === "monthly" ? (
                <Input
                  type="number"
                  value={settings?.payout_day || "1"}
                  onChange={(e) => setSettings({ ...settings, payout_day: e.target.value })}
                  className="bg-zinc-800 border-zinc-700 text-white"
                  min={1}
                  max={28}
                  placeholder="1-28"
                />
              ) : (
                <Select
                  value={settings?.payout_day || "friday"}
                  onValueChange={(value) => setSettings({ ...settings, payout_day: value })}
                >
                  <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-800 border-zinc-700">
                    <SelectItem value="monday">Monday</SelectItem>
                    <SelectItem value="tuesday">Tuesday</SelectItem>
                    <SelectItem value="wednesday">Wednesday</SelectItem>
                    <SelectItem value="thursday">Thursday</SelectItem>
                    <SelectItem value="friday">Friday</SelectItem>
                    <SelectItem value="saturday">Saturday</SelectItem>
                    <SelectItem value="sunday">Sunday</SelectItem>
                  </SelectContent>
                </Select>
              )}
            </div>

            {/* Minimum Payout */}
            <div className="space-y-2">
              <Label className="text-zinc-300">Minimum Payout Amount ($)</Label>
              <Input
                type="number"
                value={settings?.minimum_payout || 25}
                onChange={(e) => setSettings({ ...settings, minimum_payout: parseFloat(e.target.value) || 25 })}
                className="bg-zinc-800 border-zinc-700 text-white"
                min={1}
                step={5}
              />
              <p className="text-xs text-zinc-500">
                Commissions below this amount will accumulate until threshold is met
              </p>
            </div>

            {/* Auto-Approve Toggle */}
            <div className="flex items-center justify-between p-3 bg-zinc-800/50 rounded-lg">
              <div>
                <p className="text-white font-medium">Auto-Approve Enabled</p>
                <p className="text-xs text-zinc-400">Allow auto-approval of verified commissions</p>
              </div>
              <Switch
                checked={settings?.auto_approve_enabled || false}
                onCheckedChange={(checked) => setSettings({ ...settings, auto_approve_enabled: checked })}
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowSettings(false)}
              className="border-zinc-700 text-zinc-300"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSaveSettings}
              disabled={savingSettings}
              className="bg-amber-500 hover:bg-amber-600 text-black"
            >
              {savingSettings ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : null}
              Save Settings
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirm Process Dialog */}
      <Dialog open={showConfirmProcess} onOpenChange={setShowConfirmProcess}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-white max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-amber-400" />
              Not Payout Day
            </DialogTitle>
            <DialogDescription className="text-zinc-400">
              Today is not your scheduled payout day ({dashboard?.schedule?.day}). 
              Do you want to process payouts anyway?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => setShowConfirmProcess(false)}
              className="border-zinc-700 text-zinc-300"
            >
              Wait for {dashboard?.schedule?.day}
            </Button>
            <Button
              onClick={() => handleBatchProcess(true)}
              className="bg-amber-500 hover:bg-amber-600 text-black"
            >
              Process Now Anyway
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SoldierPayoutDashboard;
